import { Button } from "@amazing-care/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@amazing-care/ui/command";
import { Kbd } from "@amazing-care/ui/kbd";
import { FileText, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useDocuments } from "@/features/documents/use-documents";
import { useDepartments } from "@/features/navigation/use-departments";
import { ROUTES } from "@/routes";

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: departments } = useDepartments();
  const { data: documents } = useDocuments();

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const documentsByDepartment = useMemo(() => {
    if (!departments || !documents) return [];
    return departments
      .map((department) => ({
        department,
        documents: documents.filter((document) => document.departmentId === department.id),
      }))
      .filter((group) => group.documents.length > 0);
  }, [departments, documents]);

  function labelFor(departmentId: string, subDivisionId?: string) {
    const department = departments?.find((item) => item.id === departmentId);
    if (!department) return "";
    const subDivision = department.subDivisions?.find((item) => item.id === subDivisionId);
    return subDivision ? `${department.name} · ${subDivision.name}` : department.name;
  }

  function goToDocument(departmentId: string, documentId: string) {
    setOpen(false);
    navigate(ROUTES.document(departmentId, documentId));
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="w-48 justify-start gap-2 text-muted-foreground sm:w-64"
      >
        <Search />
        <span className="flex-1 truncate text-left">Search documents…</span>
        <Kbd>⌘K</Kbd>
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search documents"
        description="Search for a document across all departments"
      >
        <CommandInput placeholder="Search documents..." />
        <CommandList>
          <CommandEmpty>No documents found.</CommandEmpty>
          {documentsByDepartment.map(({ department, documents: docs }) => (
            <CommandGroup key={department.id} heading={department.name}>
              {docs.map((doc) => {
                const label = labelFor(doc.departmentId, doc.subDivisionId);
                return (
                  <CommandItem
                    key={doc.id}
                    value={`${doc.name} ${doc.category} ${label}`}
                    onSelect={() => goToDocument(doc.departmentId, doc.id)}
                  >
                    <FileText />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{doc.name}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {label} · {doc.category}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
